import { createContext, useContext, useState, useMemo, useEffect } from 'react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { createAppTheme } from '../theme'

// =============================================================================
// THEME CONTEXT
// =============================================================================

const ThemeContext = createContext({
  mode: 'dark',
  toggleTheme: () => {}
})

// =============================================================================
// THEME PROVIDER COMPONENT
// =============================================================================

export function ThemeProvider({ children }) {
  // Initialize from localStorage or default to 'dark'
  const [mode, setMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('powertron-theme-mode')
      return saved === 'light' ? 'light' : 'dark'
    }
    return 'dark'
  })

  // Create theme based on current mode
  const theme = useMemo(() => createAppTheme(mode), [mode])

  // Persist preference to localStorage
  useEffect(() => {
    localStorage.setItem('powertron-theme-mode', mode)
  }, [mode])

  // Toggle between dark and light
  const toggleTheme = () => {
    setMode(prev => prev === 'dark' ? 'light' : 'dark')
  }

  const contextValue = useMemo(() => ({
    mode,
    toggleTheme
  }), [mode])

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}

// =============================================================================
// HOOK
// =============================================================================

export function useThemeMode() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider')
  }
  return context
}

export default ThemeContext
